import React, { useEffect, useState } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastProps {
    id: string;
    message: string;
    type: ToastType;
    duration?: number;
    onClose: (id: string) => void;
}

/*
 * Colours: roles (docs/design/dark-mode-plan.md §4). The icon discs and glyphs
 * are their status role's tint and text. The coloured border is an accent that
 * reads on both surfaces, so it stays put.
 * Contract: docs/design/dark-mode-plan.md
 */
export const Toast: React.FC<ToastProps> = ({ id, message, type, duration = 3000, onClose }) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // Trigger enter animation
        requestAnimationFrame(() => setIsVisible(true));

        const timer = setTimeout(() => {
            setIsVisible(false);
            // Wait for exit animation to finish before removing
            setTimeout(() => onClose(id), 300);
        }, duration);

        return () => clearTimeout(timer);
    }, [id, duration, onClose]);

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(() => onClose(id), 300);
    };

    const getIcon = () => {
        switch (type) {
            case 'success':
                return (
                    <div className="w-8 h-8 rounded-full bg-primary-tint flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-primary-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                );
            case 'error':
                return (
                    <div className="w-8 h-8 rounded-full bg-danger-tint flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-danger-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </div>
                );
            case 'warning':
                return (
                    <div className="w-8 h-8 rounded-full bg-warning-tint flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-warning-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                );
            case 'info':
            default:
                return (
                    <div className="w-8 h-8 rounded-full bg-info-tint flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-info-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                );
        }
    };

    const getBorderColor = () => {
        switch (type) {
            case 'success': return 'border-green-500';
            case 'error': return 'border-red-500';
            case 'warning': return 'border-amber-500';
            case 'info': return 'border-blue-500';
        }
    };

    return (
        <div
            className={`
                pointer-events-auto transform transition-all duration-300 ease-out w-full max-w-sm px-4 md:px-0
                ${isVisible ? 'translate-y-0 opacity-100 scale-100' : '-translate-y-4 opacity-0 scale-95'}
            `}
        >
            <div className={`flex items-center p-4 rounded-xl border ${getBorderColor()} bg-surface shadow-xl min-h-[4rem]`}>
                {getIcon()}
                <div className="ml-3 mr-8 flex-1">
                    <p className={`text-sm font-medium text-ink leading-snug break-words`}>
                        {message}
                    </p>
                </div>
                <button
                    onClick={handleClose}
                    className="ml-auto -mr-2 -mt-2 p-2 text-ink-faint hover:text-ink-muted rounded-full transition-colors flex-shrink-0"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
        </div>
    );
};
